import React, { useEffect, useMemo, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { X, Save, Loader2, Plus, Minus, Download } from 'lucide-react';

/**
 * Lightweight spreadsheet editor. Loads CSV / XLSX files (raw bytes), lets the
 * user edit cells, then exports back to the same format on save.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - fileName: string (e.g. "Q3 Budget.xlsx")
 *  - mimeType: string (the mime type of `bytes`)
 *  - bytes: ArrayBuffer | Uint8Array — file contents to load
 *  - onSave: (newBlob: Blob, mimeType: string) => Promise<void>
 *  - readOnly?: boolean
 */
const SpreadsheetEditor = ({ open, onClose, fileName, mimeType, bytes, onSave, readOnly = false }) => {
  const [sheets, setSheets] = useState([]);            // [{ name, data: string[][] }]
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  const isCsv = useMemo(() => {
    if (!fileName) return false;
    if (mimeType === 'text/csv') return true;
    return fileName.toLowerCase().endsWith('.csv');
  }, [fileName, mimeType]);

  // Parse file when bytes change
  useEffect(() => {
    if (!open || !bytes) return;
    setError(null);
    try {
      const wb = XLSX.read(bytes, { type: 'array', cellStyles: false });
      const parsed = wb.SheetNames.map(name => {
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', blankrows: false });
        // Normalize so every row has the same column count
        const cols = rows.reduce((max, r) => Math.max(max, r.length), 0);
        const minCols = Math.max(cols, 8);
        const minRows = Math.max(rows.length, 20);
        const padded = [];
        for (let i = 0; i < minRows; i++) {
          const row = rows[i] || [];
          const newRow = [];
          for (let j = 0; j < minCols; j++) newRow[j] = row[j] !== undefined ? String(row[j]) : '';
          padded.push(newRow);
        }
        return { name, data: padded };
      });
      setSheets(parsed.length ? parsed : [{ name: 'Sheet1', data: makeBlankGrid(20, 8) }]);
      setActiveSheetIdx(0);
      setDirty(false);
    } catch (e) {
      console.error('Spreadsheet parse error:', e);
      setError('Could not parse this file. It may be corrupted or in an unsupported format.');
    }
  }, [bytes, open]);

  const makeBlankGrid = (rows, cols) => {
    const g = [];
    for (let i = 0; i < rows; i++) {
      const r = [];
      for (let j = 0; j < cols; j++) r.push('');
      g.push(r);
    }
    return g;
  };

  const updateCell = useCallback((sheetIdx, rowIdx, colIdx, value) => {
    setSheets(prev => prev.map((s, i) => {
      if (i !== sheetIdx) return s;
      const newData = s.data.map((row, r) =>
        r !== rowIdx ? row : row.map((c, k) => (k !== colIdx ? c : value))
      );
      return { ...s, data: newData };
    }));
    setDirty(true);
  }, []);

  const addRow = () => {
    setSheets(prev => prev.map((s, i) => {
      if (i !== activeSheetIdx) return s;
      const cols = s.data[0]?.length || 8;
      return { ...s, data: [...s.data, new Array(cols).fill('')] };
    }));
    setDirty(true);
  };

  const addCol = () => {
    setSheets(prev => prev.map((s, i) => {
      if (i !== activeSheetIdx) return s;
      return { ...s, data: s.data.map(r => [...r, '']) };
    }));
    setDirty(true);
  };

  const buildOutputBlob = () => {
    const wb = XLSX.utils.book_new();
    sheets.forEach(({ name, data }) => {
      // Trim trailing empty rows/cols
      const trimmed = trimEmpty(data);
      const ws = XLSX.utils.aoa_to_sheet(trimmed);
      XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
    });
    if (isCsv) {
      const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(trimEmpty(sheets[activeSheetIdx].data)));
      return {
        blob: new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        mime: 'text/csv',
      };
    }
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return {
      blob: new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
  };

  const trimEmpty = (rows) => {
    let lastRow = rows.length;
    while (lastRow > 0 && rows[lastRow - 1].every(c => c === '' || c === null || c === undefined)) lastRow--;
    const trimmedRows = rows.slice(0, lastRow);
    if (trimmedRows.length === 0) return [['']];
    let lastCol = 0;
    trimmedRows.forEach(r => {
      for (let i = r.length - 1; i >= 0; i--) {
        if (r[i] !== '' && r[i] !== null && r[i] !== undefined) { lastCol = Math.max(lastCol, i + 1); break; }
      }
    });
    if (lastCol === 0) lastCol = 1;
    return trimmedRows.map(r => r.slice(0, lastCol));
  };

  const handleSave = async () => {
    if (!onSave || saving) return;
    try {
      setSaving(true);
      const { blob, mime } = buildOutputBlob();
      await onSave(blob, mime);
      setDirty(false);
    } catch (e) {
      console.error('Save error:', e);
      alert(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const downloadLocal = () => {
    const { blob } = buildOutputBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'spreadsheet.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!open) return null;
  const activeSheet = sheets[activeSheetIdx];

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-2 sm:p-4" data-testid="spreadsheet-editor">
      <div className="bg-gray-950 border border-white/15 rounded-xl w-full max-w-7xl h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 bg-black">
          <div className="min-w-0 flex-1">
            <h2 className="text-white font-semibold truncate" data-testid="spreadsheet-filename">
              {fileName || 'Spreadsheet'}{dirty ? ' •' : ''}
            </h2>
            <p className="text-xs text-gray-400">
              {isCsv ? 'CSV' : 'XLSX'} · {sheets.length} sheet{sheets.length !== 1 ? 's' : ''}
              {readOnly ? ' · read only' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadLocal}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-white/10 hover:bg-white/15 border border-white/15 rounded-md transition-colors"
              data-testid="spreadsheet-download-btn"
              title="Download a copy"
            >
              <Download size={14} /> Download
            </button>
            {!readOnly && (
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                data-testid="spreadsheet-save-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-md"
              data-testid="spreadsheet-close-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Sheet tabs */}
        {sheets.length > 1 && (
          <div className="flex items-center gap-1 px-4 py-2 border-b border-white/10 bg-black/60 overflow-x-auto">
            {sheets.map((s, i) => (
              <button
                key={i}
                onClick={() => setActiveSheetIdx(i)}
                className={`px-3 py-1 text-xs rounded-md whitespace-nowrap ${
                  i === activeSheetIdx
                    ? 'bg-white text-black font-semibold'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
                data-testid={`spreadsheet-sheet-tab-${i}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-auto bg-gray-900">
          {error ? (
            <div className="p-6 text-red-400">{error}</div>
          ) : !activeSheet ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading…
            </div>
          ) : (
            <table className="border-collapse text-sm" data-testid="spreadsheet-grid">
              <thead className="sticky top-0 z-10 bg-gray-800">
                <tr>
                  <th className="w-10 border border-white/10 bg-gray-800 text-gray-400 text-xs sticky left-0 z-20"></th>
                  {activeSheet.data[0].map((_, j) => (
                    <th
                      key={j}
                      className="min-w-[120px] px-2 py-1 border border-white/10 bg-gray-800 text-gray-300 text-xs font-medium"
                    >
                      {colLabel(j)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeSheet.data.map((row, r) => (
                  <tr key={r}>
                    <td className="w-10 border border-white/10 bg-gray-800 text-gray-400 text-xs text-center sticky left-0">
                      {r + 1}
                    </td>
                    {row.map((cell, c) => (
                      <td key={c} className="border border-white/10 p-0">
                        <input
                          type="text"
                          value={cell}
                          readOnly={readOnly}
                          onChange={(e) => updateCell(activeSheetIdx, r, c, e.target.value)}
                          className="w-full px-2 py-1 bg-transparent text-white outline-none focus:bg-emerald-900/40 focus:ring-1 focus:ring-emerald-500"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer toolbar */}
        {!readOnly && !error && (
          <div className="flex items-center gap-2 px-4 py-2 border-t border-white/10 bg-black/80">
            <button
              onClick={addRow}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-white/10 hover:bg-white/15 border border-white/15 rounded-md"
              data-testid="spreadsheet-add-row-btn"
            >
              <Plus size={12} /> Row
            </button>
            <button
              onClick={addCol}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-white/10 hover:bg-white/15 border border-white/15 rounded-md"
              data-testid="spreadsheet-add-col-btn"
            >
              <Plus size={12} /> Column
            </button>
            {dirty && <span className="text-amber-400 text-xs ml-2">Unsaved changes</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const colLabel = (idx) => {
  let label = '';
  let n = idx;
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};

export default SpreadsheetEditor;
